import { useAuth, useUser } from "@clerk/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { LoadingSpinner } from "@/components/global/elements/loading-spinner"
import { PageHeader } from "@/components/layout"
import { ManageGameForm } from "@/components/manage-content/manage-game-form"
import { useOrpc } from "@/integrations/orpc"
import { useProductionEntity } from "@/queries/production/use-production-entity"
import {
	detectGameFieldDifferences,
	transformProductionToGameFormData,
	transformStagingToGameFormData,
} from "@/utils/form.helpers"

export const Route = createFileRoute("/games/$id/manage")({
	component: ManageGamePage,
})

function ManageGamePage() {
	const { id } = Route.useParams()
	const { user } = useUser()
	const userEmail =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses?.[0]?.emailAddress ??
		"unknown@cardnexus.com"
	const { client } = useOrpc()
	const { isSignedIn } = useAuth()
	const gameSlug = id

	const {
		data: stagingData,
		isLoading: stagingLoading,
		error: stagingError,
	} = useQuery({
		queryKey: ["gameAdmin", "staging", "getEntity", "game", gameSlug],
		queryFn: async () => {
			const result = await client.staging.getEntity({
				entityType: "game",
				gameSlug: gameSlug,
				entitySlug: gameSlug,
			})

			// Type guard to ensure we got a game
			if (result.entityType !== "game") {
				throw new Error(`Expected game entity, got ${result.entityType}`)
			}

			return result
		},
		enabled: isSignedIn && !!gameSlug,
	})

	const { data: productionData, isLoading: productionLoading } =
		useProductionEntity({
			gameSlug,
			entitySlug: gameSlug,
			entityType: "game",
			enabled: isSignedIn && !!gameSlug,
		})

	const stagingFormData = transformStagingToGameFormData(stagingData)
	const productionFormData = transformProductionToGameFormData(productionData)

	const fieldDifferences = detectGameFieldDifferences(
		productionFormData,
		stagingFormData,
	)

	const isLoading = stagingLoading || productionLoading

	if (isLoading) {
		return (
			<div className="flex justify-center p-6">
				<LoadingSpinner />
			</div>
		)
	}

	if (stagingError || !stagingFormData) {
		return (
			<div className="p-6">
				<PageHeader
					title="Game not found"
					description={
						stagingError
							? stagingError.message
							: `The game with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	return (
		<ManageGameForm
			productionData={productionFormData}
			stagingData={stagingFormData}
			fieldDifferences={fieldDifferences}
			gameSlug={gameSlug}
			userEmail={userEmail}
		/>
	)
}
